import { useVirtualizer } from '@tanstack/react-virtual';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useDebounceCallback, useOnClickOutside } from 'usehooks-ts';
import { InputAutoCompleteMultipleWithAccessorI } from './input-autocomplete-multiple-with-accessors.interface';
import Chip from '../chip/chip';
import Icon from '../icon/icon';
import {
    ACCORDION_PADDING,
    MIN_OPTION_HEIGHT,
    MIN_OPTION_VISIBLE,
} from '../input-autocomplete/input-autocomplete.constants';
import {
    filterQuery,
    handleAutocompleteRemoteQuery,
    renderOptionWithAccessor,
} from '../input-autocomplete/input-autocomplete.utils';
import { getInputContainerClasses } from '../input/input';

export const InputAutoCompleteMultipleWithAccessor = <T,>({
    name,
    label,
    testId,
    value,
    options,
    isLoading = false,
    minVisible = MIN_OPTION_VISIBLE,
    optionDropdownClasses,
    inputClassName,
    labelClasses,
    optionClasses,
    containerClasses,
    debounceTime = 3000,
    filterRemote,
    accessorValue,
    accessorLabel,
    onChange,
    onInputChange,
    onScrollToEnd,
    callback,
    wrapPills,
    errorMessage,
    pillCount = 3,
    selectAllOption = false,
    required = false,
    valueUpdateable = false,
    disableSelection = false,
    hasDeviceField,
    noDeviceMessage,
    tachographIcon,
    singleSelection,
    ...props
}: InputAutoCompleteMultipleWithAccessorI<T>) => {
    const refMain = useRef<HTMLDivElement | null>(null);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const calledQuery = useRef<string>('');
    const optionsHasValues = options && options.length > 0;
    const optionsExist = options === undefined;
    const shouldUpdateValueInit = useRef<boolean>(optionsExist || !optionsHasValues || valueUpdateable);

    // options change control
    const optionLengthControl = useRef<{ continue: boolean; length: number }>({
        continue: true,
        length: options?.length,
    });

    // this will search values in options by value key
    // and return a list of labels according what it find
    const getLabelFromValueList = useCallback(
        (values: string[]) => {
            if (!values) return [];

            return options
                .filter((item) => values.includes(item[accessorValue as keyof T] as unknown as string))
                .map((item) => item[accessorLabel as keyof T] as unknown as string);
        },
        [accessorLabel, accessorValue, options],
    );

    const getLabelFromValue = useCallback(
        (value: string) => {
            // this function require options to be different of null
            // to return a value according key value, if options is
            // an async value you should handle an update with useEffect

            if (!value || value === '') return '';

            // Check if value is in options
            const foundItem = options.find(
                (item) => (item[accessorValue as keyof T] as unknown as string) === value,
            ) as T;

            if (!foundItem) {
                return '';
            }
            // Return label of found item
            return foundItem[accessorLabel as keyof T] as unknown as string;
        },
        [accessorLabel, accessorValue, options],
    );

    const [show, setShow] = useState(false);
    const [query, setQuery] = useState<string>('');
    const [valueSelected, setValueSelected] = useState<string[]>(getLabelFromValueList(value as string[]) ?? []);

    // Handle click outside
    useOnClickOutside(refMain, () => setShow(false));

    // Debounce query
    const debounced = useDebounceCallback(
        (value) => {
            if (value.length && options.length) {
                callback && callback(value);
            }
        },
        debounceTime,
        { leading: true },
    );

    // Options handling empty option
    const filteredOptions = useMemo(() => {
        return valueSelected.map((i) => i.toLowerCase()).includes(query.toLowerCase())
            ? options
            : filterQuery(options, query, accessorLabel, accessorValue);
    }, [accessorLabel, accessorValue, valueSelected, options, query]);

    const dynamicMinOptions = useMemo(() => {
        return minVisible < filteredOptions.length ? minVisible : filteredOptions.length;
    }, [minVisible, filteredOptions]);

    const dynamicHeight = useMemo(() => {
        return show
            ? filteredOptions.length
                ? MIN_OPTION_HEIGHT * (dynamicMinOptions + (selectAllOption ? 1 : 0)) + ACCORDION_PADDING * 2
                : 0
            : 0;
    }, [filteredOptions, dynamicMinOptions, show, selectAllOption]);

    const rowVirtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => modalRef.current,
        estimateSize: () => MIN_OPTION_HEIGHT,
    });

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setQuery(newText);
        onInputChange && onInputChange(newText);
    };

    const handleSelect = (value: string) => {
        const newValue = getLabelFromValue(value);
        setValueSelected((prevState) => {
            if (prevState.includes(newValue)) {
                return (prevState as string[]).filter((item) => item !== newValue);
            }
            if (singleSelection) {
                return [newValue];
            }
            return [...prevState, newValue];
        });
        setQuery('');

        onChange && onChange(value);
    };

    // handle trigger callback
    useEffect(() => {
        handleAutocompleteRemoteQuery({
            query,
            filteredOptionsLength: filteredOptions.length,
            isLoading,
            filterRemote,
            calledQueryRef: calledQuery,
            shouldOpenDropdown:
                filteredOptions.length > 0 &&
                !show &&
                query.length > 0 &&
                !valueSelected.map((i) => i.toLocaleLowerCase()).includes(query.toLocaleLowerCase()),
            openDropdown: () => setShow(true),
            debounced,
        });
    }, [filteredOptions, valueSelected, filterRemote, debounced, isLoading, query, show]);

    // handle scroll end
    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem) {
            return;
        }

        const isAtEndOfList = lastItem.index === filteredOptions.length - 1;
        const optionLengthHasChanged = optionLengthControl.current.continue;
        const filteredGotQuery = query
            ? filteredOptions.some((item) =>
                  (item[accessorLabel as keyof typeof item] as unknown as string)
                      .toLocaleLowerCase()
                      .includes(query.toLocaleLowerCase()),
              )
            : false;
        if (isAtEndOfList && !isLoading && optionLengthHasChanged && !filteredGotQuery) {
            onScrollToEnd && onScrollToEnd();
            optionLengthControl.current = {
                ...optionLengthControl.current,
                continue: false,
            };
        } else if (!optionLengthControl.current.continue && optionLengthControl.current.length < options.length) {
            optionLengthControl.current = {
                continue: true,
                length: options.length,
            };
        }

        // Do NOT delete eslint bypass, change rowVirtualizer.getVirtualItems() to rowVirtualizer
        // it makes useeffect doesnt work properly
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        rowVirtualizer.getVirtualItems(),
        filteredOptions,
        options,
        onScrollToEnd,
        isLoading,
    ]);

    useEffect(() => {
        if (options && options.length > 0 && value && value?.length > 0 && shouldUpdateValueInit.current) {
            setValueSelected(getLabelFromValueList(value));
            shouldUpdateValueInit.current = valueUpdateable;
        }
    }, [getLabelFromValueList, options, value, valueUpdateable]);

    const optionsContainerClasses = twMerge(
        'overflow-auto w-full shadow-lg border border-gray-50 rounded-b-lg bg-white relative z-50 ',
        show && filteredOptions.length > 0 && 'py-2',
        optionDropdownClasses,
    );
    const optionItemClasses = twMerge(
        'flex justify-start items-center pl-2 font-light hover:bg-gray-50',
        optionClasses,
    );

    const multipleDeleteSelected = (valueToDelete: string) => {
        setValueSelected((prevState) => (prevState as string[]).filter((value) => value !== valueToDelete));
        const key = accessorLabel ? accessorLabel : accessorValue;
        const value = (options.find((item) => (item[key as keyof T] as unknown as string) === valueToDelete) as T)[
            accessorValue as keyof T
        ];
        onChange && onChange(value as unknown as string);
    };

    const handleMultipleDeleteSelected = (evt?: React.MouseEvent) => {
        const chipValue = evt?.currentTarget?.parentElement?.querySelector('div:first-of-type')?.textContent;
        if (chipValue) multipleDeleteSelected(chipValue);
    };

    const handleMultipleDeleteAllSelected = (_evt?: React.MouseEvent) => {
        setValueSelected([]);
        const returnValues = options
            .filter((item) => {
                if (accessorLabel) {
                    return valueSelected.includes(item[accessorLabel as keyof T] as unknown as string);
                } else if (accessorValue) {
                    return valueSelected.includes(item[accessorValue as keyof T] as unknown as string);
                }
                return valueSelected.includes(item as unknown as string);
            })
            .map((value) => {
                if (accessorValue) {
                    return (value as any)[accessorValue] as string;
                }
                return value as unknown as string;
            });
        props?.onAllSelected && props?.onAllSelected([]);
    };

    const handleSelectAll = () => {
        const newValues = options.map((value) => {
            if (accessorLabel && accessorValue) {
                return (value as any)[accessorLabel];
            }
            if (accessorValue) {
                return (value as any)[accessorValue];
            }
            return value;
        });
        if (newValues.every((item) => valueSelected.includes(item))) {
            setValueSelected([]);
        } else {
            setValueSelected(newValues);
        }

        const returnValues = options.map((value) => {
            if (accessorValue) {
                return (value as any)[accessorValue] as string;
            }
            return value as unknown as string;
        });

        props?.onAllSelected && props?.onAllSelected(returnValues);
    };

    //Reset value selected on value change
    useEffect(() => {
        if (value?.length === 0 && valueSelected.length > 0) {
            setValueSelected([]);
        }
    }, [name, value, valueSelected, valueSelected.length]);

    return (
        <div
            className={twMerge(containerClasses, props.disabled && 'cursor-auto')}
            data-testid={testId || `${name}-autocomplete`}
            data-test-id={testId || `${name}-autocomplete`}
            ref={refMain}
        >
            <div className="flex flex-col w-full">
                {label && (
                    <label className={twMerge('mb-1 text-sm font-medium text-black', labelClasses)} htmlFor={name}>
                        {label}
                        {required && <span className="text-red-600"> * </span>}
                    </label>
                )}
                <div
                    className={twMerge(
                        getInputContainerClasses({ ...props }),
                        'flex gap-1 justify-between overflow-hidden',
                        wrapPills && 'flex-wrap',
                        errorMessage && 'border-red-600',
                        inputClassName,
                        props.disabled && 'cursor-auto bg-gray-50',
                    )}
                    onClick={(evt) => {
                        if (!props.disabled) {
                            evt.currentTarget.querySelector('input')?.focus();
                            setShow((prevState) => !prevState);
                        }
                    }}
                    onKeyDown={(evt) => {
                        if (!props.disabled && evt.key === 'Enter') {
                            evt.currentTarget.querySelector('input')?.focus();
                            setShow((prevState) => !prevState);
                        }
                    }}
                    role="button"
                    tabIndex={props.disabled ? -1 : 0}
                    aria-expanded={show}
                >
                    {(valueSelected as string[]).length <= pillCount ? (
                        (valueSelected as string[]).map((value: string) => (
                            <Chip
                                className="m-0 shrink-0"
                                text={value}
                                size="xxs"
                                onClose={handleMultipleDeleteSelected}
                                disabled={(props.disabled || disableSelection) ?? false}
                            />
                        ))
                    ) : (
                        <Chip
                            className="m-0 shrink-0 grow-0"
                            text={`${(valueSelected as string[]).length} seleccionados`}
                            size="xxs"
                            onClose={handleMultipleDeleteAllSelected}
                            disabled={(props.disabled || disableSelection) ?? false}
                        />
                    )}
                    <input
                        className="outline-none shrink w-full max-w-[calc(100%_-_28px)] disabled:cursor-auto disabled:bg-gray-50"
                        name={name}
                        value={query}
                        onChange={handleInputChange}
                        placeholder={props.placeholder}
                        data-test-id={`${name}-combo-input`}
                        disabled={props.disabled ?? false}
                        autoComplete="one-time-code"
                    />
                    {isLoading ? (
                        <div
                            className={twMerge(
                                ' transition-all absolute right-2',
                                isLoading && 'text-primary animate-spin',
                            )}
                        >
                            <Icon name="ri-loader-4-line" className="text-stone-500" size={14} />
                        </div>
                    ) : (
                        !props.disabled && (
                            <div
                                className={twMerge(
                                    'transition bg-white h-5 w-9 rounded-full px-2 absolute right-2 flex items-center justify-center',
                                    show && 'rotate-180',
                                )}
                                data-test-id="arrow-down"
                            >
                                <Icon name="ri-arrow-down-s-line" className="text-stone-500" size={14} />
                            </div>
                        )
                    )}
                </div>
            </div>
            <div
                ref={modalRef}
                className={optionsContainerClasses}
                style={
                    dynamicHeight === 0
                        ? { height: 0, padding: 0, border: 'none' }
                        : { height: dynamicHeight, display: 'block' }
                }
                data-testid={`${name}-autocomplete-container`}
            >
                <div
                    className="leading-none"
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {selectAllOption && !singleSelection && (
                        <button
                            onClick={handleSelectAll}
                            className="flex w-full justify-start items-center pl-8 hover:bg-gray-50 h-8"
                        >
                            Seleccionar todo
                        </button>
                    )}
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        return renderOptionWithAccessor({
                            name,
                            item: filteredOptions[virtualRow.index] as T,
                            keyLabel: accessorLabel,
                            keyValue: accessorValue,
                            style: {
                                position: 'absolute',
                                top: selectAllOption ? virtualRow.size : 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualRow.size}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            },
                            classes: optionItemClasses,
                            action: handleSelect,
                            disabled: disableSelection,
                            multiple: true,
                            selected: valueSelected,
                            hasDeviceField: hasDeviceField,
                            noDeviceMessage: noDeviceMessage,
                            tachographIcon: tachographIcon,
                        });
                    })}
                </div>
            </div>
        </div>
    );
};

export default InputAutoCompleteMultipleWithAccessor;
